import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, type Control, type FieldValues } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  toast,
} from '@kognitos/lattice';

import type {
  Bdkv2BookAuthenticationDescriptor,
  Bdkv2BookDescriptor,
} from '@/generated/hooks/bdk/models';
import { getListSecretsQueryKey } from '@/generated/hooks/gringotts/v1/services/secret_service';
import { useGetSecretType } from '@/generated/hooks/gringotts/v1/services/secret_type_service';
import {
  replaceCredentialValue,
  type TCredential,
} from '@/modules/credentials';
import { captureException } from '@/modules/sentry';

import { createAuthenticationCredentialsSchema } from '../schemas';
import { getKeyValueCredentialIds } from '../utils';
import { hasInvalidKeyValueCredentials } from './CredentialKeyValueInput';
import { CustomCredentialsStep } from './CustomCredentialsStep';
import {
  EmptyCredentialValuesConfirmDialog,
  useEmptyCredentialValuesConfirmation,
} from './EmptyCredentialValuesConfirmDialog';

/** Props for {@link EditCredentialValuesDialog}. */
export interface IEditCredentialValuesDialogProps {
  organizationId: string;
  workspaceId: string;
  integration: Bdkv2BookDescriptor;
  /** The key/value auth method the credential is for. */
  authentication: Bdkv2BookAuthenticationDescriptor;
  /** The credential to edit; `null` keeps the dialog closed. */
  credential: TCredential | null;
  onClose: () => void;
}

/**
 * Replaces a key/value credential's values. They can't be read back, so the
 * fields start empty and every required one must be filled.
 *
 * @param props - Component props.
 * @returns The dialog.
 */
export function EditCredentialValuesDialog({
  organizationId,
  workspaceId,
  integration,
  authentication,
  credential,
  onClose,
}: IEditCredentialValuesDialogProps) {
  const { t } = useTranslation('credentials');
  const { t: tCommon } = useTranslation('common');
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);

  const fields = useMemo(
    () => authentication.custom?.credentials ?? [],
    [authentication],
  );
  const schema = useMemo(
    () => z.object({ values: createAuthenticationCredentialsSchema(fields) }),
    [fields],
  );
  const form = useForm<{ values: Record<string, string> }>({
    resolver: zodResolver(schema),
    defaultValues: { values: {} },
  });
  const { data: secretTypeData } = useGetSecretType(
    authentication.secret_type_id ?? '',
    { query: { enabled: !!credential && !!authentication.secret_type_id } },
  );
  const keyValueIds = getKeyValueCredentialIds(
    integration,
    authentication.custom?.id ?? '',
  );
  const hasInvalidKeys = hasInvalidKeyValueCredentials(
    form.watch(keyValueIds.map(id => `values.${id}` as const)),
  );
  const { confirmEmptyValues, dialogProps: emptyValuesDialogProps } =
    useEmptyCredentialValuesConfirmation();

  const close = () => {
    form.reset({ values: {} });
    onClose();
  };

  const write = async (values: Record<string, string>) => {
    if (!credential?.id) return;
    setIsSaving(true);
    try {
      await replaceCredentialValue(
        organizationId,
        workspaceId,
        credential,
        values,
        secretTypeData?.data.schema,
      );
      toast.success(t('credentials.connection.updated'));
      void queryClient.invalidateQueries({
        queryKey: getListSecretsQueryKey(organizationId),
      });
      close();
    } catch (error) {
      captureException(error as Error, {
        tags: { module: 'integrations', action: 'updateCredentialValues' },
      });
      toast.error(t('credentials.errors.updateFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const save = async () => {
    if (!(await form.trigger())) return;
    const { values } = form.getValues();
    confirmEmptyValues(
      keyValueIds.map(id => values[id]),
      () => void write(values),
    );
  };

  return (
    <Dialog open={!!credential} onOpenChange={open => !open && close()}>
      <DialogContent data-testid="edit-credential-values-dialog">
        <DialogHeader>
          <DialogTitle>{t('credentials.editValues.title')}</DialogTitle>
          <DialogDescription>
            {t('credentials.editValues.description', {
              name: credential?.display_name || credential?.name || '',
            })}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <CustomCredentialsStep
            integration={integration}
            authMethodId={authentication.custom?.id ?? ''}
            control={form.control as unknown as Control<FieldValues>}
            fieldPrefix="values"
          />
        </Form>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={isSaving}
            onClick={close}
          >
            {tCommon('common.action.cancel')}
          </Button>
          <Button
            type="button"
            disabled={hasInvalidKeys}
            isLoading={isSaving}
            onClick={() => void save()}
          >
            {t('credentials.connection.saveCredential')}
          </Button>
        </DialogFooter>
        <EmptyCredentialValuesConfirmDialog {...emptyValuesDialogProps} />
      </DialogContent>
    </Dialog>
  );
}
